import { useState, type ReactNode } from 'react';
import { Popover } from 'antd';
import { MAX_REACTIONS_PER_USER, REACTION_EMOJIS, type ReactionSummary } from '@lens/shared';
import styles from './ReactionBar.module.css';

interface ReactionPickerProps {
  reactions: ReactionSummary[];
  onToggle: (emoji: string) => void;
  disabled?: boolean;
  /** Элемент-триггер (кнопка), по клику на который открывается сетка emoji. */
  children: ReactNode;
  placement?: 'top' | 'topLeft' | 'topRight' | 'bottom' | 'bottomLeft' | 'bottomRight';
}

/**
 * Поповер-сетка выбора emoji. Активные (мои) подсвечены; при достигнутом лимите
 * новые emoji заблокированы, снять активную — можно. Общий для карточки и
 * hover-действий комментария.
 */
export function ReactionPicker({
  reactions,
  onToggle,
  disabled = false,
  children,
  placement = 'topLeft',
}: ReactionPickerProps): JSX.Element {
  const [open, setOpen] = useState(false);

  const myCount = reactions.filter((r) => r.reactedByMe && r.count > 0).length;
  const atLimit = myCount >= MAX_REACTIONS_PER_USER;

  const content = (
    <div className={styles.picker}>
      {REACTION_EMOJIS.map((emoji) => {
        const active = reactions.some((r) => r.emoji === emoji && r.reactedByMe && r.count > 0);
        // Новую emoji нельзя добавить при исчерпанном лимите; снять активную — можно.
        const blocked = !active && atLimit;
        return (
          <button
            key={emoji}
            type="button"
            className={`${styles.pickerBtn} ${active ? styles.pickerBtnActive : ''}`}
            disabled={blocked || disabled}
            onClick={() => {
              onToggle(emoji);
              setOpen(false);
            }}
            aria-label={active ? `Убрать реакцию ${emoji}` : `Поставить реакцию ${emoji}`}
          >
            {emoji}
          </button>
        );
      })}
      {atLimit && (
        <div className={styles.pickerHint}>
          Не более {MAX_REACTIONS_PER_USER} реакций. Снимите одну, чтобы добавить другую.
        </div>
      )}
    </div>
  );

  return (
    <Popover
      content={content}
      trigger="click"
      open={open}
      onOpenChange={(next) => !disabled && setOpen(next)}
      placement={placement}
      destroyTooltipOnHide
    >
      {children}
    </Popover>
  );
}
